///
/// EarthBrowser
///
/// Saved Views Browser Feature
///
/// Manages the saved-views browser window lifecycle and command palette
/// registration ("Saved Views" opens the manage window).
///
/// Created by Matt Giger on 9/12/2026
/// Copyright (c) 2026 Geodata Labs
///

import { useEffect } from 'react'
import { Bookmark } from 'lucide-react'
import { ViewsBrowserRenderer } from '@/components/features/views-browser/ViewsBrowser'
import { CommandRegistry } from '@/components/features/CommandRegistry'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { openOrRestoreWindow } from '@/utils/windowUtils'

export const VIEWS_WINDOW_ID = 'saved-views-browser'
const VIEWS_WINDOW_WIDTH = 760
const VIEWS_WINDOW_HEIGHT = 540

/** Open (or restore) the saved-views browser window */
export function openViewsBrowser(
	dispatch: Parameters<typeof openOrRestoreWindow>[0],
	datasetWindows: Parameters<typeof openOrRestoreWindow>[1]
): void {
	openOrRestoreWindow(
		dispatch,
		datasetWindows,
		VIEWS_WINDOW_ID,
		{
			id: VIEWS_WINDOW_ID,
			title: 'Saved Views',
			windowType: 'saved-views',
			x: 140,
			y: 110,
			width: VIEWS_WINDOW_WIDTH,
			height: VIEWS_WINDOW_HEIGHT,
			isMaximized: false,
			isVisible: true,
		}
	)
}

export function ViewsBrowserFeature() {
	const dispatch = useAppDispatch()
	const datasetWindows = useAppSelector(state => state.dialog.datasetWindows)
	const viewsWindow = datasetWindows[VIEWS_WINDOW_ID]

	// Register with command palette
	useEffect(() => {
		const unregister = CommandRegistry.registerFeature({
			id: 'saved-views-browser',
			command: {
				id: 'saved-views',
				title: 'Saved Views',
				icon: <Bookmark className="w-4 h-4 text-cyan-400" />,
				category: 'Views',
				enabled: true,
				onSelect: () => {
					openViewsBrowser(dispatch, datasetWindows)
				}
			},
			renderBrowser: () => null // Window-based browser
		})

		return unregister
	}, [dispatch, datasetWindows])

	if (!viewsWindow) {
		return null
	}

	return (
		<ViewsBrowserRenderer
			windowId={VIEWS_WINDOW_ID}
			windowState={viewsWindow}
		/>
	)
}
